// external
import React, { FC, useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Dialog, InputAdornment, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DownhillSkiingIcon from '@mui/icons-material/DownhillSkiing';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import ShieldIcon from '@mui/icons-material/Shield';
import { useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';

// internal
import './teams.page.css';
import useService from '../../hooks/service.hook';
import TeamsService from '../../services/teams.service';
import ProgramsService from '../../services/programs.service';
import { TeamPayload } from '../../models/service.models';
import {
  BillingInterval,
  BillingPlanCard,
  BillingPlanKey,
  BillingPlansResponse,
  Program,
} from '../../models/billing.models';
import COETeamCardComponent from '../../components/coe-team-card/coe-team-card.component';
import COEWardTeamCardComponent from '../../components/coe-ward-team-card.component.tsx/coe-ward-team-card.component';
import { Team } from '../../models/training.models';
import { User } from '../../models/user.models';
import GuardianService from '../../services/guardian.service';
import JoinTeamStep, { CODE_LENGTH } from '../../components/coe-onboarding-modal/JoinTeamStep';
import TeamJoinSentStep from '../../components/coe-onboarding-modal/TeamJoinSentStep';
import { palette } from '../../theme';
import { isAcademicEmail, isEarlyAccessTrial, isUnlimitedTeamCreator } from '../../helpers/billing.helpers';
import CreateTeamDialog from '../../components/dialogs/create-team.dialog';
import NewTeamPicker from '../../components/new-team-picker/new-team-picker.component';
import PlanPicker from '../create-team/components/plan-picker.component';
import PlanIncludes from '../create-team/components/plan-includes.component';

type JoinTeamModalStep = 'form' | 'sent';

export interface TeamsPageProps {
  /** Rendered at /create-team: opens the Create a team modal over My Teams. */
  openCreateTeam?: boolean;
}

const TeamsPage: FC<TeamsPageProps> = ({ openCreateTeam = false }) => {
  const teamsService = useService(TeamsService);
  const programsService = useService(ProgramsService);
  const guardianService = useService(GuardianService);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [teams, setTeams] = useState<TeamPayload[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [wardTeams, setWardTeams] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>('');
  const [newTeamMenuAnchor, setNewTeamMenuAnchor] = useState<null | HTMLElement>(null);
  const [joinTeamModalOpen, setJoinTeamModalOpen] = useState(false);
  const [joinTeamModalStep, setJoinTeamModalStep] = useState<JoinTeamModalStep>('form');
  const [teamCode, setTeamCode] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [teamCodeError, setTeamCodeError] = useState(false);
  const [isTeamJoinLoading, setIsTeamJoinLoading] = useState(false);
  const [createTeamOpen, setCreateTeamOpen] = useState(openCreateTeam);
  const [plans, setPlans] = useState<BillingPlanCard[]>([]);
  const [planOffer, setPlanOffer] = useState<Omit<BillingPlansResponse, 'plans'> | null>(null);
  const [plansLoading, setPlansLoading] = useState(false);
  const [pickedPlan, setPickedPlan] = useState<BillingPlanKey | null>('program');
  const [planIntervals, setPlanIntervals] = useState<Partial<Record<BillingPlanKey, BillingInterval>>>({
    small_team: 'month',
    program: 'month',
    academic: 'year',
  });
  const [dialogPlan, setDialogPlan] = useState<BillingPlanKey | null>(null);
  const [inlineCode, setInlineCode] = useState('');
  const [inlineCodeError, setInlineCodeError] = useState(false);
  const [inlineJoining, setInlineJoining] = useState(false);

  const accentColor = palette.primary.main;

  useEffect(() => {
    getTeams();
  }, []);

  useEffect(() => {
    if (openCreateTeam) setCreateTeamOpen(true);
  }, [openCreateTeam]);

  const closeCreateTeam = () => {
    setCreateTeamOpen(false);
    setDialogPlan(null);
    if (openCreateTeam) navigate('/teams', { replace: true });
  };

  const getTeams = () => {
    setLoading(true);
    Promise.all([
      teamsService.getTeams(),
      guardianService.getWardTeams(),
      programsService.listPrograms().catch(() => null),
    ])
      .then(([teamsResponse, wardTeamsResponse, programResponse]) => {
        setLoading(false);
        setTeams(teamsResponse.data.data);
        updateUserRoleStatus(teamsResponse.data.data);
        setWardTeams(wardTeamsResponse.data.data);
        setPrograms(programResponse?.data?.data || []);
      })
      .catch(() => {
        setLoading(false);
        enqueueSnackbar('There was an error while getting your teams. Please try again later', {
          autoHideDuration: 3000,
          variant: 'error',
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
      });
  };

  const updateUserRoleStatus = (teams: Team[]) => {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    const coachedTeamsCount = teams.filter((team: Team) => team.role === 'coach');
    user.role = coachedTeamsCount.length > 0 ? 'coach' : 'athlete';
    window.localStorage.setItem('user', JSON.stringify(user));
  };

  const q = search.trim().toLowerCase();

  const activeTeams = teams
    .filter((t: any) => t.status === 'active')
    .filter((t: any) => !q || t.name?.toLowerCase().includes(q));

  const filteredWardTeams = wardTeams.filter((t: any) => !q || t.team_name?.toLowerCase().includes(q));

  const foundingOwner = isUnlimitedTeamCreator();
  const legacyUnlimitedClosed =
    teams.some((t: any) => t.plan === 'unlimited' && t.status !== 'cancelled' && t.status !== 'canceled') &&
    programs.length === 0 &&
    !foundingOwner;

  const hasCoach = activeTeams.some((t: any) => t.role === 'coach');
  const hasAthlete = activeTeams.some((t: any) => t.role === 'athlete');

  // No team, no supervised team, and no subscription: show the plans instead of an empty page.
  const startWithPlans =
    !loading &&
    !foundingOwner &&
    !teams.some((t: any) => t.status === 'active') &&
    wardTeams.length === 0 &&
    programs.every((program) => program.status === 'incomplete');

  useEffect(() => {
    if (!startWithPlans || plans.length > 0 || plansLoading) return;
    setPlansLoading(true);
    programsService
      .getBillingPlans()
      .then((response) => {
        const { plans: planCards, ...offer } = response.data.data;
        setPlans(planCards);
        setPlanOffer(offer);
      })
      .catch(() => {
        enqueueSnackbar('Unable to load plans. Please try again later', {
          autoHideDuration: 3000,
          variant: 'error',
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
      })
      .finally(() => setPlansLoading(false));
  }, [startWithPlans]);

  const trialLabel =
    (planOffer?.trialDays ?? 0) <= 0
      ? null
      : isEarlyAccessTrial(planOffer?.trialTier)
      ? '6 month free trial'
      : '1 month free trial';

  const continueWithPlan = () => {
    if (!pickedPlan) return;
    if (pickedPlan === 'academic' && !isAcademicEmail()) {
      enqueueSnackbar('Academic requires an email address that ends with .edu.', {
        autoHideDuration: 3000,
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
      });
      return;
    }
    setDialogPlan(pickedPlan);
    setCreateTeamOpen(true);
  };

  const resetJoinTeamModal = () => {
    setJoinTeamModalOpen(false);
    setJoinTeamModalStep('form');
    setTeamCode(Array(CODE_LENGTH).fill(''));
    setTeamCodeError(false);
    setIsTeamJoinLoading(false);
  };

  const openJoinTeamModal = () => {
    setNewTeamMenuAnchor(null);
    setJoinTeamModalStep('form');
    setTeamCode(Array(CODE_LENGTH).fill(''));
    setTeamCodeError(false);
    setIsTeamJoinLoading(false);
    setJoinTeamModalOpen(true);
  };

  const handleJoinTeam = () => {
    setTeamCodeError(false);
    setIsTeamJoinLoading(true);
    teamsService
      .postTeamJoinRequest({ inviteCode: teamCode.join(''), requestType: 'athlete' })
      .then(() => {
        setIsTeamJoinLoading(false);
        setJoinTeamModalStep('sent');
      })
      .catch(() => {
        setIsTeamJoinLoading(false);
        setTeamCodeError(true);
      });
  };

  const joinWithInlineCode = (event: React.FormEvent) => {
    event.preventDefault();
    if (inlineCode.length !== CODE_LENGTH || inlineJoining) return;
    setInlineCodeError(false);
    setInlineJoining(true);
    teamsService
      .postTeamJoinRequest({ inviteCode: inlineCode, requestType: 'athlete' })
      .then(() => {
        setInlineCode('');
        setJoinTeamModalStep('sent');
        setJoinTeamModalOpen(true);
      })
      .catch(() => setInlineCodeError(true))
      .finally(() => setInlineJoining(false));
  };

  const renderInviteCodeJoin = () => (
    <form className="teams-start-code" onSubmit={joinWithInlineCode}>
      <span className="teams-start-code-icon" aria-hidden>
        <GroupsOutlinedIcon />
      </span>
      <div className="teams-start-code-copy">
        <b>Join your team</b>
        <span>
          {inlineCodeError
            ? 'Code not found. Double-check with your coach and try again.'
            : 'Enter the 8-character invite code from your coach.'}
        </span>
      </div>
      <div className="teams-start-code-row">
        <input
          className={`teams-start-code-input${inlineCodeError ? ' error' : ''}`}
          value={inlineCode}
          onChange={(event) => {
            setInlineCodeError(false);
            setInlineCode(
              event.target.value
                .replace(/[^a-zA-Z0-9]/g, '')
                .toUpperCase()
                .slice(0, CODE_LENGTH)
            );
          }}
          placeholder="ABCD1234"
          aria-label="Team invite code"
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
        />
        <Button
          type="submit"
          variant="contained"
          disableElevation
          disabled={inlineCode.length !== CODE_LENGTH || inlineJoining}
          sx={{
            textTransform: 'none',
            borderRadius: '10px',
            fontWeight: 700,
            minWidth: 96,
            height: 44,
            '&.Mui-disabled': { backgroundColor: '#bcd0f7', color: '#fff' },
          }}
        >
          {inlineJoining ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Join team'}
        </Button>
      </div>
    </form>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <CircularProgress style={{ width: 150, height: 150 }} />
        </div>
      );
    }

    if (startWithPlans) {
      return (
        <div className="teams-start">
          <h2 className="teams-start-title">Choose a plan for your first team</h2>
          <p className="teams-start-sub">
            {trialLabel
              ? 'Every plan has every feature. Your first team starts with a free trial, with no card.'
              : 'Every plan has every feature. The team is created after the card payment succeeds.'}
          </p>
          {plansLoading ? (
            <div className="teams-start-loading">
              <CircularProgress size={40} />
            </div>
          ) : plans.length === 0 ? (
            <div className="teams-start-actions">
              <Button
                variant="contained"
                onClick={() => setCreateTeamOpen(true)}
                sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 700, px: 3 }}
              >
                Create a team
              </Button>
              {renderInviteCodeJoin()}
            </div>
          ) : (
            <>
              <PlanPicker
                plans={plans}
                selectedKey={pickedPlan}
                intervals={planIntervals}
                trialLabel={trialLabel}
                onSelect={setPickedPlan}
                onIntervalChange={(key, interval) => setPlanIntervals((current) => ({ ...current, [key]: interval }))}
              />
              <div className="teams-start-actions">
                <Button
                  variant="contained"
                  onClick={continueWithPlan}
                  disabled={!pickedPlan}
                  sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 700, px: 3 }}
                >
                  Continue
                </Button>
              </div>
              {renderInviteCodeJoin()}
              <PlanIncludes />
            </>
          )}
        </div>
      );
    }

    if (teams.length === 0 && wardTeams.length === 0) {
      return (
        <div className="teams-empty-state-container">
          <DownhillSkiingIcon style={{ width: 250, height: 250, color: 'grey' }} />
          <h2 style={{ margin: 10 }}>Start Building Your Team</h2>
          <p style={{ margin: 10, textAlign: 'center' }}>
            No teams were found. Create or join a team to start assigning trainings and tracking workouts
          </p>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            endIcon={<KeyboardArrowDownIcon />}
            onClick={(e) => setNewTeamMenuAnchor(e.currentTarget)}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              px: 2,
              mt: 1.25,
            }}
          >
            New Team
          </Button>
        </div>
      );
    }

    if (activeTeams.length === 0 && filteredWardTeams.length === 0) {
      return (
        <div className="teams-empty-state-container">
          <DownhillSkiingIcon style={{ width: 250, height: 250, color: 'grey' }} />
          <h2 style={{ margin: 10 }}>Start Building Your Team</h2>
          <p style={{ margin: 10, textAlign: 'center' }}>
            {q
              ? 'No teams match your search.'
              : 'No active teams yet. Create or join a team to start assigning trainings and tracking workouts.'}
          </p>
          {!q ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              endIcon={<KeyboardArrowDownIcon />}
              onClick={(e) => setNewTeamMenuAnchor(e.currentTarget)}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                px: 2,
                mt: 1.25,
              }}
            >
              New Team
            </Button>
          ) : null}
        </div>
      );
    }

    return (
      <>
        {/* My Teams section */}
        {activeTeams.length > 0 && (
          <>
            <div className="teams-section-header">
              <Typography variant="h6" fontWeight={700}>
                My Teams
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  backgroundColor: '#E5E7EB',
                  borderRadius: '12px',
                  px: 1,
                  py: 0.15,
                  fontWeight: 600,
                  color: '#374151',
                  fontSize: '0.8rem',
                }}
              >
                {activeTeams.length}
              </Typography>
            </div>

            {/* Role legend */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
              {hasCoach && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#2563EB' }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Coach
                  </Typography>
                </Box>
              )}
              {hasAthlete && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#16A34A' }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Athlete
                  </Typography>
                </Box>
              )}
            </Box>

            <div className="teams-card-container">
              {activeTeams.map((teamInfo: any) => (
                <COETeamCardComponent
                  key={teamInfo.id}
                  teamId={teamInfo.id}
                  name={teamInfo.name}
                  description={teamInfo.description}
                  color={teamInfo.color}
                  status={teamInfo.status}
                  plan={teamInfo.plan ?? 'free'}
                  role={teamInfo.role ?? 'coach'}
                  athleteCount={teamInfo.athlete_count}
                  coachCount={teamInfo.coach_count}
                  sessionCount={teamInfo.session_count}
                  billingRequired={Boolean(teamInfo.billingRequired)}
                />
              ))}
            </div>
          </>
        )}

        {/* Supervised Teams section */}
        {filteredWardTeams.length > 0 && (
          <>
            <div className="teams-section-header" style={{ marginTop: 32 }}>
              <Typography variant="h6" fontWeight={700}>
                Supervised Teams
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  backgroundColor: '#E5E7EB',
                  borderRadius: '12px',
                  px: 1,
                  py: 0.15,
                  fontWeight: 600,
                  color: '#374151',
                  fontSize: '0.8rem',
                }}
              >
                {filteredWardTeams.length}
              </Typography>
              <Chip
                icon={<ShieldIcon sx={{ fontSize: '14px !important' }} />}
                label="Guardian"
                size="small"
                sx={{
                  backgroundColor: '#FFF7ED',
                  color: '#F97316',
                  border: '1px solid #FED7AA',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 24,
                  '& .MuiChip-icon': { color: '#F97316' },
                }}
              />
            </div>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, mt: -0.5 }}>
              Teams you monitor as a parent or guardian
            </Typography>

            <div className="teams-card-container">
              {filteredWardTeams.map((teamInfo: any) => (
                <COEWardTeamCardComponent
                  key={teamInfo.team_id}
                  teamId={teamInfo.team_id}
                  name={teamInfo.team_name}
                  description={teamInfo.team_description}
                  color={teamInfo.team_color}
                  status="active"
                  wards={teamInfo.wards}
                  plan={teamInfo.plan ?? 'free'}
                  athleteCount={teamInfo.athlete_count}
                  coachCount={teamInfo.coach_count}
                  sessionCount={teamInfo.session_count}
                />
              ))}
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <div className={window.localStorage.getItem('sidedrawer') ? 'page-full' : 'page'} id="page">
      <div className="teams-root">
        {/* Page header */}
        <div className="teams-page-header">
          {loading || startWithPlans ? null : (
            <div className="teams-page-header-actions">
              <TextField
                className="teams-search-input"
                size="small"
                placeholder="Search teams..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#9CA3AF', fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: '#FFFFFF',
                  },
                }}
              />
              <Button variant="text" className="teams-billing-chip" onClick={() => navigate('/billing')}>
                {programs.some(
                  (item) =>
                    item.status === 'unpaid' ||
                    item.status === 'incomplete' ||
                    (item.status === 'trialing' && item.trialEndsAt && dayjs(item.trialEndsAt).isBefore(dayjs()))
                )
                  ? 'Payment due'
                  : programs.length > 1
                  ? `${programs.length} subscriptions`
                  : programs.length === 1
                  ? `${programs[0].planName} · ${
                      programs[0].status === 'active'
                        ? 'Billed'
                        : programs[0].status === 'trialing'
                        ? 'Free trial'
                        : 'Billing'
                    }`
                  : teams.some(
                      (t: any) => t.plan === 'unlimited' && t.status !== 'cancelled' && t.status !== 'canceled'
                    )
                  ? 'Unlimited · Legacy'
                  : 'Billing'}
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                endIcon={<KeyboardArrowDownIcon />}
                onClick={(e) => setNewTeamMenuAnchor(e.currentTarget)}
                sx={{
                  textTransform: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  px: 2,
                }}
              >
                New Team
              </Button>
              <NewTeamPicker
                anchorEl={newTeamMenuAnchor}
                onClose={() => setNewTeamMenuAnchor(null)}
                createLabel={
                  foundingOwner ? 'Add a squad' : legacyUnlimitedClosed ? 'View Unlimited plan' : 'Create new team'
                }
                createDescription={
                  foundingOwner
                    ? 'Members are included on your Unlimited plan'
                    : legacyUnlimitedClosed
                    ? 'See your current Unlimited teams'
                    : 'Start a team and choose a plan'
                }
                onCreate={() => {
                  setNewTeamMenuAnchor(null);
                  if (legacyUnlimitedClosed) {
                    navigate('/billing');
                    return;
                  }
                  setCreateTeamOpen(true);
                }}
                onJoin={() => {
                  setNewTeamMenuAnchor(null);
                  openJoinTeamModal();
                }}
              />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="teams-page">{renderContent()}</div>
      </div>

      <Dialog
        open={joinTeamModalOpen}
        onClose={isTeamJoinLoading ? undefined : resetJoinTeamModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: { xs: 2, sm: 3 } } }}
      >
        {joinTeamModalStep === 'form' ? (
          <JoinTeamStep
            accentColor={accentColor}
            teamCode={teamCode}
            hasError={teamCodeError}
            isLoading={isTeamJoinLoading}
            onChange={(idx, val) => {
              setTeamCodeError(false);
              setTeamCode((prev) => {
                const next = [...prev];
                next[idx] = val;
                return next;
              });
            }}
            onJoin={handleJoinTeam}
            onSkip={resetJoinTeamModal}
            onBack={resetJoinTeamModal}
          />
        ) : (
          <TeamJoinSentStep accentColor={accentColor} onContinue={resetJoinTeamModal} />
        )}
      </Dialog>

      <CreateTeamDialog
        open={createTeamOpen}
        onClose={closeCreateTeam}
        initialPlanKey={dialogPlan}
        initialInterval={dialogPlan ? planIntervals[dialogPlan] : undefined}
      />
    </div>
  );
};

export default TeamsPage;
